import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ImagePreviewItem {
  src: string;
  fullSrc?: string;
  fallbackSrc?: string;
}

interface ImagePreviewGridProps {
  images: Array<string | ImagePreviewItem>;
  altPrefix: string;
  gridClassName?: string;
  thumbClassName?: string;
  maxVisible?: number;
  onRetryImage?: () => void;
}

export function ImagePreviewGrid({
  images,
  altPrefix,
  gridClassName = 'grid grid-cols-2 gap-3 sm:grid-cols-3',
  thumbClassName = 'h-40',
  maxVisible,
  onRetryImage,
}: ImagePreviewGridProps) {
  // Keep selection, not a snapshot of a signed URL that can expire while open.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [failed, setFailed] = useState<Record<string, true>>({});
  const [attempt, setAttempt] = useState(0);
  const visibleImages = maxVisible ? images.slice(0, maxVisible) : images;
  const hiddenCount = maxVisible && images.length > maxVisible ? images.length - maxVisible : 0;
  const normalize = (image: string | ImagePreviewItem): ImagePreviewItem => typeof image === 'string' ? { src: image } : image;
  const selected = previewIndex === null || !images[previewIndex] ? null : normalize(images[previewIndex]);
  const fullSrc = selected && (selected.fullSrc || selected.fallbackSrc || selected.src);
  const previewLabel = `${altPrefix} ${(previewIndex ?? 0) + 1}`;
  const markFailed = (src: string) => setFailed((previous) => ({ ...previous, [src]: true }));
  const retry = () => {
    setFailed({});
    setAttempt((value) => value + 1);
    onRetryImage?.();
  };

  useEffect(() => {
    if (previewIndex === null) return;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setPreviewIndex(null); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewIndex]);

  return (
    <>
      <div className={gridClassName}>
        {visibleImages.map((image, index) => {
          const item = normalize(image);
          // Never download fullSrc as an implicit thumbnail fallback.
          const fallbackSrc = item.fallbackSrc || item.src;
          const visibleSrc = failed[item.src] ? fallbackSrc : item.src;
          const label = `${altPrefix} ${index + 1}`;
          const showMore = hiddenCount > 0 && index === visibleImages.length - 1;
          return (
            <button
              key={index}
              type="button"
              aria-label={failed[visibleSrc] ? `${label} 加载失败，重试` : `查看${label}`}
              onClick={(event) => {
                event.stopPropagation();
                if (failed[visibleSrc]) retry();
                else setPreviewIndex(index);
              }}
              className="group relative block overflow-hidden rounded-md border border-gray-200 bg-gray-50 text-left transition-colors hover:border-primary/50"
            >
              {failed[visibleSrc] ? (
                <span className={`${thumbClassName} flex w-full items-center justify-center px-1 text-center text-[10px] text-primary`}>加载失败，重试</span>
              ) : (
                <img
                  key={`${visibleSrc}-${attempt}`}
                  src={visibleSrc}
                  alt={label}
                  loading="lazy"
                  decoding="async"
                  className={`${thumbClassName} w-full object-contain`}
                  onError={() => markFailed(visibleSrc)}
                />
              )}
              {showMore && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-xs font-medium text-white">+{hiddenCount}</span>
              )}
            </button>
          );
        })}
      </div>

      {fullSrc && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-label={previewLabel}
          onClick={(event) => {
            event.stopPropagation();
            if (event.target === event.currentTarget) setPreviewIndex(null);
          }}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-md bg-white/90 px-3 py-2 text-sm font-medium text-gray-900 shadow-sm hover:bg-white"
            onClick={(event) => { event.stopPropagation(); setPreviewIndex(null); }}
          >关闭</button>
          {failed[fullSrc] ? (
            <button type="button" onClick={(event) => { event.stopPropagation(); retry(); }} className="rounded bg-white px-4 py-3 text-sm text-gray-900">
              图片加载失败，点击重试
            </button>
          ) : (
            <img
              key={`${fullSrc}-${attempt}`}
              src={fullSrc}
              alt={previewLabel}
              className="max-h-[88vh] max-w-[92vw] object-contain"
              onError={() => markFailed(fullSrc)}
              onClick={(event) => event.stopPropagation()}
            />
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
